import React from 'react';
import { AlertTriangle, Target } from 'lucide-react';

export type ScoutingContext = {
  target_type?: 'opponent' | 'self' | 'both' | 'unknown' | string;
  target_team?: string | null;
  user_team?: string | null;
  opponent_team?: string | null;
  confidence?: 'high' | 'medium' | 'low' | 'unknown' | string;
  source?: string;
  team_match_rate?: number;
  customer_message?: string;
};

function bannerTone(confidence?: string, targetType?: string): string {
  if (confidence === 'unknown' || confidence === 'low' || targetType === 'unknown') {
    return 'border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-900/20';
  }
  if (targetType === 'both') {
    return 'border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-900/20';
  }
  return 'border-primary-200 bg-primary-50 dark:border-primary-700 dark:bg-primary-900/20';
}

function headline(context: ScoutingContext): string {
  const target = context.target_team;
  switch (context.target_type) {
    case 'opponent':
      return target ? `Scouting Target: Opponent — ${target}` : 'Scouting Target: Opponent';
    case 'self':
      return target ? `Scouting Target: My Team — ${target}` : 'Scouting Target: My Team';
    case 'both':
      return 'Scouting Mode: Both Teams';
    case 'unknown':
    default:
      return 'Scouting Target: Unknown';
  }
}

function subtext(context: ScoutingContext): string {
  if (context.customer_message) {
    return context.customer_message;
  }
  switch (context.target_type) {
    case 'self':
      return 'Scoutlytics is analyzing your team for self-scout tendencies.';
    case 'both':
      return 'This report may include tendencies from both sides of the matchup.';
    case 'unknown':
      return 'Scoutlytics could not confirm which team to isolate. Tendencies may include both teams.';
    default:
      return '';
  }
}

interface ScoutingContextBannerProps {
  context?: ScoutingContext | null;
}

const ScoutingContextBanner: React.FC<ScoutingContextBannerProps> = ({ context }) => {
  if (!context || !context.target_type) {
    return null;
  }

  const warn = context.confidence === 'low' || context.confidence === 'unknown' || context.target_type === 'unknown';
  const Icon = warn ? AlertTriangle : Target;

  return (
    <div
      className={`card border-2 mb-6 ${bannerTone(context.confidence, context.target_type)}`}
      data-testid="scouting-context-banner"
    >
      <div className="flex items-start gap-3">
        <Icon className={`h-5 w-5 mt-0.5 shrink-0 ${warn ? 'text-amber-600 dark:text-amber-400' : 'text-primary-600 dark:text-primary-300'}`} />
        <div>
          <h2 className="text-lg font-semibold text-secondary-900 dark:text-white">{headline(context)}</h2>
          <p className="mt-1 text-sm text-secondary-700 dark:text-secondary-300 leading-relaxed">{subtext(context)}</p>
          {context.team_match_rate != null && context.confidence !== 'unknown' && (
            <p className="mt-2 text-xs text-secondary-500 dark:text-secondary-400">
              Target match rate: {(context.team_match_rate * 100).toFixed(0)}% of reviewed frames
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScoutingContextBanner;

export { headline, subtext, bannerTone };
